/**
 * WorkflowLinkMatrix — Tier-3 Disclosure Engine M4.
 *
 * Manages `(WorkflowCode, StepCode) → Templates[]` rows. Surfaces both:
 *   - A flat editable table (default view) showing every link
 *   - An "Add link" form that pulls templates from the catalog
 *
 * Wires endpoints:
 *   POST   /v3/admin/disclosures/workflow-links
 *   DELETE /v3/admin/disclosures/workflow-links/{workflowCode}/{stepCode}/{templateCode}
 *
 * The composite envelope (`/v3/config/disclosures`) does NOT include
 * workflow-link rows today (they're stored in `DisclosureWorkflowLinks` —
 * not yet projected into the read envelope). The list view here loads from
 * `envelope` if present (forward-compat), otherwise renders an empty state
 * with the form still functional for adds.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Plus, RefreshCw, Search, Trash2, Edit3 } from 'lucide-react';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import {
  upsertLink,
  deleteLink,
  type DisclosureWorkflowLinkUpsertInput,
} from '@/services/disclosuresAdminApi';

import {
  Checkbox,
  ConfirmDialog,
  Field,
  FieldGrid,
  Modal,
  NumberInput,
  Select,
  TextArea,
  TextInput,
  Toast,
  useToast,
} from './_shared';

interface LinkRow {
  workflowCode: string;
  stepCode: string;
  templateCode: string;
  isMandatory?: boolean;
  conditionExpression?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
  notes?: string | null;
}

interface FormShape {
  workflowCode: string;
  stepCode: string;
  templateCode: string;
  isMandatory: boolean;
  conditionExpression: string;
  sortOrder: string;
  isActive: boolean;
  notes: string;
}

const EMPTY_FORM: FormShape = {
  workflowCode: '',
  stepCode: '',
  templateCode: '',
  isMandatory: true,
  conditionExpression: '',
  sortOrder: '0',
  isActive: true,
  notes: '',
};

function rowToForm(r: LinkRow | null): FormShape {
  if (!r) return EMPTY_FORM;
  return {
    workflowCode: r.workflowCode ?? '',
    stepCode: r.stepCode ?? '',
    templateCode: r.templateCode ?? '',
    isMandatory: r.isMandatory ?? true,
    conditionExpression: r.conditionExpression ?? '',
    sortOrder: r.sortOrder !== null && r.sortOrder !== undefined ? String(r.sortOrder) : '0',
    isActive: r.isActive !== false,
    notes: r.notes ?? '',
  };
}

function formToInput(f: FormShape): DisclosureWorkflowLinkUpsertInput {
  return {
    workflowCode: f.workflowCode.trim(),
    stepCode: f.stepCode.trim(),
    templateCode: f.templateCode.trim(),
    isMandatory: f.isMandatory,
    conditionExpression: f.conditionExpression.trim() || null,
    sortOrder: f.sortOrder.trim() ? Number(f.sortOrder) : 0,
    isActive: f.isActive,
    notes: f.notes.trim() || null,
  };
}

export function WorkflowLinkMatrix(): JSX.Element {
  const { envelope, state, error, refresh } = useDisclosuresConfig();
  const { toast, push, clear } = useToast();
  const [filter, setFilter] = useState<string>('');
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<LinkRow | null>(null);
  const [form, setForm] = useState<FormShape>(EMPTY_FORM);
  const [busy, setBusy] = useState<boolean>(false);
  const [inlineErr, setInlineErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LinkRow | null>(null);

  useEffect(() => {
    if (editorOpen) {
      setForm(rowToForm(editing));
      setInlineErr(null);
    }
  }, [editorOpen, editing]);

  // Forward-compat: read links from envelope if surfaced. Backend may not
  // currently include this slice (`workflowLinks` key); the editor is still
  // useful for adds.
  const links = useMemo<ReadonlyArray<LinkRow>>(() => {
    const raw = (envelope as unknown as Record<string, unknown>)['workflowLinks'];
    if (!Array.isArray(raw)) return [];
    return raw.map((r: unknown): LinkRow => {
      const o = (r ?? {}) as Record<string, unknown>;
      return {
        workflowCode: String(o['workflowCode'] ?? ''),
        stepCode: String(o['stepCode'] ?? ''),
        templateCode: String(o['templateCode'] ?? ''),
        isMandatory: Boolean(o['isMandatory']),
        conditionExpression:
          (o['conditionExpression'] as string | null | undefined) ?? null,
        sortOrder:
          o['sortOrder'] === null || o['sortOrder'] === undefined
            ? null
            : Number(o['sortOrder']),
        isActive: o['isActive'] !== false,
        notes: (o['notes'] as string | null | undefined) ?? null,
      };
    });
  }, [envelope]);

  const filtered = useMemo<ReadonlyArray<LinkRow>>(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) => {
      const haystack =
        `${l.workflowCode}|${l.stepCode}|${l.templateCode}|${l.conditionExpression ?? ''}|${l.notes ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [links, filter]);

  const templateCodes = useMemo<string[]>(() => {
    const s = new Set<string>();
    for (const t of envelope.templates) {
      const c = String(t.code ?? '');
      if (c) s.add(c);
    }
    return Array.from(s).sort();
  }, [envelope.templates]);

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const canSave =
    !busy &&
    form.workflowCode.trim() !== '' &&
    form.stepCode.trim() !== '' &&
    form.templateCode.trim() !== '';

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    setBusy(true);
    setInlineErr(null);
    try {
      const result = await upsertLink(formToInput(form));
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setInlineErr(msg);
        push('err', msg);
        return;
      }
      push(
        'ok',
        `Link ${form.workflowCode}/${form.stepCode} → ${form.templateCode} saved.`,
      );
      setEditorOpen(false);
      setEditing(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  };

  const onDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!confirmDelete) return;
    const wc = confirmDelete.workflowCode;
    const sc = confirmDelete.stepCode;
    const tc = confirmDelete.templateCode;
    if (!wc || !sc || !tc) {
      setConfirmDelete(null);
      return;
    }
    setBusy(true);
    try {
      const result = await deleteLink(wc, sc, tc);
      if (!result.success) {
        push('err', result.error ?? 'Delete failed.');
        return;
      }
      push('ok', `Link ${wc}/${sc} → ${tc} deleted.`);
      setConfirmDelete(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  }, [confirmDelete, push, refresh]);

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures" className="back-link">Disclosure Engine</Link>
        <span aria-hidden="true"> / </span>
        <span>Workflow links</span>
      </nav>

      <Link to="/disclosures" className="back-link">
        <ArrowLeft size={14} /> Back to Disclosure Engine
      </Link>

      <section className="page-header">
        <h1 className="page-header__title">Workflow Link Matrix</h1>
        <p className="page-header__subtitle page-intro">
          (WorkflowCode, StepCode) → Templates[] mapping. Determines which
          disclosures attach at each pipeline step.
        </p>
      </section>

      <header className="card-header" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={14} aria-hidden="true" />
          <TextInput
            placeholder="Filter by workflow / step / template…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ minWidth: 280, width: 280 }}
          />
        </div>
        <div className="toolbar__spacer" style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void refresh()}
          disabled={state === 'loading'}
        >
          <RefreshCw size={14} /> {state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          <Plus size={14} /> Add link
        </button>
      </header>

      {error && <div className="banner banner--err" role="alert">{error}</div>}

      {filtered.length === 0 && (
        <div className="empty">
          {links.length === 0
            ? 'No workflow links surfaced in the read envelope. The Add link form below remains functional — saved links will appear after the next config-route projection.'
            : 'No links match the current filter.'}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="table-wrap">
          <table className="table table--striped table--hover">
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Step</th>
                <th>Template</th>
                <th>Mandatory</th>
                <th>Condition</th>
                <th>Sort</th>
                <th>Active</th>
                <th>Notes</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr key={`${l.workflowCode}-${l.stepCode}-${l.templateCode}-${i}`}>
                  <td><code className="cell-id">{l.workflowCode}</code></td>
                  <td><code>{l.stepCode}</code></td>
                  <td><code>{l.templateCode}</code></td>
                  <td>
                    {l.isMandatory ? (
                      <span className="pill pill--info">Mandatory</span>
                    ) : (
                      <span className="pill pill--muted">Optional</span>
                    )}
                  </td>
                  <td>
                    {l.conditionExpression ? (
                      <code style={{ fontSize: 11 }}>{l.conditionExpression}</code>
                    ) : '—'}
                  </td>
                  <td>{l.sortOrder ?? 0}</td>
                  <td>
                    {l.isActive === false ? (
                      <span className="pill pill--muted">Inactive</span>
                    ) : (
                      <span className="pill pill--ok">Active</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12 }}>{l.notes ?? '—'}</td>
                  <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setEditing(l);
                        setEditorOpen(true);
                      }}
                    >
                      <Edit3 size={12} /> Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setConfirmDelete(l)}
                      style={{ color: 'var(--pcb-red)' }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={editorOpen}
        onClose={() => {
          if (!busy) {
            setEditorOpen(false);
            setEditing(null);
          }
        }}
        title={
          editing
            ? `Edit link ${form.workflowCode}/${form.stepCode} → ${form.templateCode}`
            : 'Add workflow link'
        }
        width={620}
        closeOnBackdrop={!busy}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setEditorOpen(false);
                setEditing(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submit()}
              disabled={!canSave}
            >
              {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
            </button>
          </>
        }
      >
        {inlineErr && (
          <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
            {inlineErr}
          </div>
        )}
        <FieldGrid columns={2}>
          <Field label="Workflow code" required>
            <TextInput
              value={form.workflowCode}
              onChange={(e) => set('workflowCode', e.target.value)}
              readOnly={editing !== null}
              placeholder="e.g. CART_PIPELINE"
            />
          </Field>
          <Field label="Step code" required>
            <TextInput
              value={form.stepCode}
              onChange={(e) => set('stepCode', e.target.value)}
              readOnly={editing !== null}
              placeholder="e.g. INITIATE_SIGNING"
            />
          </Field>
          <Field label="Template code" required hint="Pick from the catalog." span={2}>
            <Select
              value={form.templateCode}
              onChange={(e) => set('templateCode', e.target.value)}
              disabled={editing !== null}
              aria-label="Template code"
              title="Template code"
            >
              <option value="">— pick a template —</option>
              {templateCodes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </Field>
          <Field label="Condition expression" hint="Boolean DSL — gating clause." span={2}>
            <TextInput
              value={form.conditionExpression}
              onChange={(e) => set('conditionExpression', e.target.value)}
              placeholder="e.g. cart.products.any(p => p.category == 'CD')"
            />
          </Field>
          <Field label="Sort order">
            <NumberInput
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
            />
          </Field>
          <Field label="Flags">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Checkbox
                label="Mandatory"
                checked={form.isMandatory}
                onChange={(e) => set('isMandatory', e.target.checked)}
              />
              <Checkbox
                label="Active"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
            </div>
          </Field>
          <Field label="Notes" span={2}>
            <TextArea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
            />
          </Field>
        </FieldGrid>
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete workflow link"
        message={
          <span>
            Soft-delete link{' '}
            <code>{confirmDelete?.workflowCode ?? ''}</code> /{' '}
            <code>{confirmDelete?.stepCode ?? ''}</code> →{' '}
            <code>{confirmDelete?.templateCode ?? ''}</code>?
          </span>
        }
        busy={busy}
        onConfirm={() => void onDeleteConfirm()}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}
